import React, { useCallback, useMemo, useState } from "react";
import DateConversion from "../../../../../utils/DateConversion";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import {
  formatAmountComma,
  formatAmountForInput,
  validateAmountValue,
  parseAmountNumber,
} from "../../../../../utils/decimalAmountUtils";
import { Checkbox } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { updateOngoingDescription } from "../../../../../store/slices/ProjectOngoing/ProjectOngoingUpdateSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchWorkEntriesProjectTracking } from "../../../../../store/slices/workentriesSlice";
import { fetchDescriptionTasksProjectTracking } from "../../../../../store/slices/DescriptionTask";
import { useSnackbar } from "notistack";
import {
  markDescriptionComplete,
  markNotDescriptionComplete,
} from "../../../../../store/slices/ProjectOngoing/projectOngoingSlice";
import LoadingSpinner from "../../../../../components/common/loader/LoadingSpinner";
import ProgressTracker from "../../../../../utils/ProgressTracker";
import useToolGuideSync from "../../../../../features/toolGuide/useToolGuideSync";
import { MANAGER_BUCKET_KEYS } from "../../../../../features/toolGuide/toolGuideConfig";
import {
  resolveTrainingAssignedDate,
  resolveTrainingEndDate,
  isTrainingExpenseDisplayRow,
  consolidateTrainingExpenseRowsForDisplay,
  applyMyTrainingsDateBoundsToTrainingRows,
  getLiveTrainingRollupForOpsAssigneeProject,
  taskMatchesOpsManagerResourceOtherExpenses,
  trainingMatchesEmployeeProject,
  buildProjectTrainingLinkMetaForRow,
  unwrapTrainings,
  parseSavedOngoingExpenseRaw,
  getOngoingExpenseNumberForTracker,
  filterInstallationOngoingOtherExpenseDisplayRows,
} from "../../../../../utils/trainingOtherExpenseSync";

const getOngoingExpenseInputDisplay = (entry, draft) => {
  if (draft != null && String(draft).trim() !== "") return draft;
  const saved = parseSavedOngoingExpenseRaw(entry);
  if (saved == null) return "";
  return formatAmountForInput(saved);
};

const isClientOnlyOngoingDescriptionRow = (entryOrId) => {
  const e =
    typeof entryOrId === "string" ? { _id: entryOrId } : entryOrId || {};
  if (e._isTrainingCost || e._prmSyntheticTrainingExpense || e.readOnly) {
    return true;
  }
  const id = String(e._id ?? "");
  return (
    id.startsWith("training_cost_") ||
    id.startsWith("prm-synthetic") ||
    id.startsWith("prm-training-rollup")
  );
};

const OpTaskTrackOtherExpense = ({
  matchedHardwareDescriptionsPartsOP,
  data,
  row,
  setIsLoading,
  selectedEmployee,
  isViewOnly = false,
}) => {
  const { formatDateByCountry, getDatePlaceholder, getDateFormat } =
    DateConversion();
  const currency = useCurrencySymbol();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { syncToolGuideStep } = useToolGuideSync(
    MANAGER_BUCKET_KEYS.PROJECT_ONGOING,
    "L4_PM"
  );
  const { mytrainings } = useSelector((state) => state.myTrainings);
  const { projects } = useSelector((state) => state.projects);
  const [loadingRows, setLoadingRows] = useState({});

  const [updatedDateDescription, setUpdatedDateDescription] = useState({});
  const [editedDescriptionNotes, setEditedDescriptionNotes] = useState({});

  const scopedDescriptionTasks = useMemo(() => {
    const base = Array.isArray(matchedHardwareDescriptionsPartsOP)
      ? matchedHardwareDescriptionsPartsOP
      : [];
    const scoped = base.filter((task) =>
      taskMatchesOpsManagerResourceOtherExpenses(task, row, null)
    );
    return filterInstallationOngoingOtherExpenseDisplayRows(scoped, row);
  }, [matchedHardwareDescriptionsPartsOP, row]);

  const { bounds: myTrainingsDateBounds, lockedTrainingRow } = useMemo(() => {
    const personId = String(selectedEmployee?._id ?? selectedEmployee?.id ?? "").trim();
    const live = getLiveTrainingRollupForOpsAssigneeProject({
      projectRow: row,
      assigneeEmp: selectedEmployee,
      trainingsEnvelope: mytrainings,
      projects,
      currencySymbol: currency,
    });

    const list = unwrapTrainings(mytrainings);
    const projectMeta = {
      ...buildProjectTrainingLinkMetaForRow(row, projects),
      assigneeEmail: String(selectedEmployee?.EmpEmail ?? "").trim().toLowerCase(),
    };
    const matchedTrainings = list.filter(
      (t) => personId && trainingMatchesEmployeeProject(t, personId, projectMeta)
    );
    const normalized = matchedTrainings.map((t) => ({
      startdate: resolveTrainingAssignedDate(t, row?.DateRequested),
      enddate: resolveTrainingEndDate(t),
    }));
    const startDateCandidate =
      normalized.map((x) => x.startdate).filter(Boolean).sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime()
      )[0] ?? null;
    const endDateCandidate =
      normalized.map((x) => x.enddate).filter(Boolean).sort(
        (a, b) => new Date(b).getTime() - new Date(a).getTime()
      )[0] ?? null;
    const bounds =
      startDateCandidate || endDateCandidate
        ? { startdate: startDateCandidate, enddate: endDateCandidate }
        : null;

    const total = Number(live?.amount ?? 0);
    if (!Number.isFinite(total) || total <= 0) {
      return { bounds, lockedTrainingRow: null };
    }

    return {
      bounds,
      lockedTrainingRow: {
        _id: `training_cost_${personId || "selected"}`,
        taskownerid: personId,
        description: live?.description || "Training Cost",
        value: total,
        notes: live?.notes || "",
        startdate: startDateCandidate,
        enddate: endDateCandidate,
        isTaskComplete: true,
        expenses: total,
        _isTrainingCost: true,
      },
    };
  }, [mytrainings, row, selectedEmployee, projects, currency]);

  const displayDescriptionList = useMemo(() => {
    const base = scopedDescriptionTasks;
    const hasPersistedTraining = base.some((r) => isTrainingExpenseDisplayRow(r));
    if (hasPersistedTraining) {
      return applyMyTrainingsDateBoundsToTrainingRows(
        consolidateTrainingExpenseRowsForDisplay(base),
        myTrainingsDateBounds
      );
    }
    return lockedTrainingRow ? [...base, lockedTrainingRow] : base;
  }, [scopedDescriptionTasks, lockedTrainingRow, myTrainingsDateBounds]);

  const handleDescriptionDateSubmit = useCallback(
    async (entry) => {
      if (isViewOnly) return;
      if (isClientOnlyOngoingDescriptionRow(entry)) return;
      syncToolGuideStep("view-details");
      const matchedEntry = updatedDateDescription[entry._id] || {};
      const matchedStartDate = matchedEntry.startdate || null;
      const matchedEndDate = matchedEntry.enddate || null;
      const updatedData = {
        description: entry.description,
        value: entry.value,
        notes: entry.notes,
        typeservice: entry.typeservice,
        startdate: matchedStartDate ?? entry.startdate ?? null,
        enddate: matchedEndDate ?? entry.enddate ?? null,
      };
      try {
        dispatch(
          updateOngoingDescription({
            projectId: row?._id,
            id: entry._id,
            updatedData,
          })
        );
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
      } catch {
        enqueueSnackbar("Failed To Add Date", { variant: "error" });
      } finally {
        setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
        setIsLoading(false);
      }
    },
    [
      updatedDateDescription,
      row,
      dispatch,
      enqueueSnackbar,
      setLoadingRows,
      setIsLoading,
      isViewOnly,
      syncToolGuideStep,
    ]
  );

  const handleDescriptionDateChange = useCallback(
    (date, field, entry) => {
      if (isViewOnly) return;
      if (isClientOnlyOngoingDescriptionRow(entry)) return;
      setUpdatedDateDescription((prev) => ({
        ...prev,
        [entry._id]: {
          ...prev[entry._id],
          [field]: date,
        },
      }));
      if (field === "enddate") {
        const newentry = {
          ...entry,
          enddate: date,
        };
        handleDescriptionDateSubmit(newentry);
      }
    },
    [handleDescriptionDateSubmit, isViewOnly]
  );

  const handleDescriptionNotesUpdate = async (entryId) => {
    if (isViewOnly) return;
    if (isClientOnlyOngoingDescriptionRow({ _id: entryId })) return;
    syncToolGuideStep("view-details");
    const value = editedDescriptionNotes[entryId]; // get the value for this specific task
    if (!entryId) return;
    const updatedData = {
      notes: value,
    };
    try {
      await dispatch(
        updateOngoingDescription({
          projectId: row?._id,
          id: entryId,
          updatedData,
        })
      );
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch {
      enqueueSnackbar("Failed To Add Date", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
      setIsLoading(false);
    }
  };

  const [editedDescription, setEditedDescription] = useState({});

  const handleDescriptionExpensesUpdate = async (entryId) => {
    if (isViewOnly) return;
    if (isClientOnlyOngoingDescriptionRow({ _id: entryId })) return;
    syncToolGuideStep("view-details");
    const value = editedDescription[entryId]; // get the value for this specific task
    if (!entryId) return;

    const updatedData = {
      expenses: parseAmountNumber(value),
    };
    try {
      await dispatch(
        updateOngoingDescription({
          projectId: row?._id,
          id: entryId,
          updatedData,
        })
      );
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch {
      enqueueSnackbar("Failed To Add Date", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
      setIsLoading(false);
    }
  };

  const handlemarkDescriptionComplete = async (entry) => {
    if (isViewOnly) return;
    if (isClientOnlyOngoingDescriptionRow(entry)) return;
    syncToolGuideStep("view-details");
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markDescriptionComplete({ projectid, taskid }));
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Description Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  const handlemarkNotDescriptionComplete = async (entry) => {
    if (isViewOnly) return;
    if (isClientOnlyOngoingDescriptionRow(entry)) return;
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markNotDescriptionComplete({ projectid, taskid }));
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Description Not-Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
        <div className="z-[50] sticky top-0 flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-words whitespace-normal">
          <div className="p-1 w-[10%] text-center">
            Description 
          </div>
          <div className="p-1 w-[10%] text-center">
            Amount ({currency})
          </div>
          <div className="p-1 w-[13%] text-center">
            Start
            Date
          </div>
          <div className="p-1 w-[13%]">End Date</div>
                    <div className="p-1 w-[10%] text-center">
            Expenses ({currency})
          </div>
          <div className="p-1 w-[10%]">Notes</div>

          <div className="p-1 w-[24%] text-center">
            Tracker
          </div>
          <div className="p-1 w-[10%] text-center">
            Action
          </div>
        </div>
        <div className="flex text-[10px] break-all whitespace-normal max-h-14">
          <div className="p-1 border-b border-[#dee2e6] w-[10%] flex justify-center align-middle items-center">
            {"Total"}
          </div>
          <div className="p-1 border-b border-[#dee2e6] w-[10%] flex justify-center align-middle items-center">
            {formatAmountComma(
              displayDescriptionList.reduce(
                (sum, item) => sum + parseAmountNumber(item?.value),
                0
              )
            )}
          </div>

          <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center"></div>
          <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center"></div>
          <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center flex justify-center align-middle items-center">
            {formatAmountComma(
              displayDescriptionList.reduce(
                (sum, item) => sum + parseAmountNumber(item?.expenses),
                0
              )
            )}
          </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
          <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
            {displayDescriptionList.every(
              (entry) =>
                entry.isTaskComplete || isClientOnlyOngoingDescriptionRow(entry)
            ) ? (
              <>
                <span className="">
                  {(() => {
                    const hoursAssigned =
                      displayDescriptionList.reduce(
                        (sum, item) => sum + (item?.value || 0),
                        0
                      );
                    const hoursSpent = displayDescriptionList.reduce(
                      (sum, item) =>
                        sum +
                        (isClientOnlyOngoingDescriptionRow(item)
                          ? Number(item?.expenses ?? item?.value ?? 0) || 0
                          : parseSavedOngoingExpenseRaw(item) ?? 0),
                      0
                    );

                    const trackerPercentage =
                      hoursAssigned > 0 ? (hoursSpent / hoursAssigned) * 100 : 0;

                    const displayValue =
                      trackerPercentage < 100 ? 100 : trackerPercentage;

                    return <ProgressTracker value={displayValue} />;
                  })()}
                </span>
              </>
            ) : (
              <>
                <span className="">
                  {(() => {
                    const allEntries = displayDescriptionList?.reduce(
                      (sum, item) => sum + (item?.value || 0),
                      0
                    );
                    const completedEntriesCount =
                      displayDescriptionList?.reduce(
                        (sum, item) =>
                          sum +
                          (isClientOnlyOngoingDescriptionRow(item)
                            ? Number(item?.expenses ?? item?.value ?? 0) || 0
                            : parseSavedOngoingExpenseRaw(item) ?? 0),
                        0
                      );
                    const trackerPercentage =
                      allEntries > 0
                        ? (completedEntriesCount / allEntries) * 100
                        : 0;
                    return <ProgressTracker value={trackerPercentage} />;
                  })()}
                </span>
              </>

            )}
          </div>

          <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
        </div>
        {displayDescriptionList.length > 0 ? (
          displayDescriptionList.map((entry, index) => (
            <div
              key={entry}
              className="flex text-[10px] break-all whitespace-normal max-h-14"
            >
              <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                {entry.description}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                {formatAmountComma(entry.value)}
              </div>

              <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center">
                {/* {entry.startdate ? formatDateByCountry(entry.startdate) : ""} */}
                {entry._isTrainingCost || entry.isTaskComplete ? (
                  <div className="text-black">
                    {entry.startdate
                      ? formatDateByCountry(entry.startdate)
                      : ""}
                  </div>
                ) : (
                  <DatePicker
                    dateFormat={getDateFormat()}
                    disabled={isViewOnly}
                    className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md bg-white ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                    placeholderText={getDatePlaceholder()}
                    popperClassName="z-[1500]"
                    minDate={
                      entry.startdate
                        ? new Date(entry.startdate)
                        : row?.DateRequested
                          ? new Date(row.DateRequested)
                          : null
                    }
                    maxDate={new Date()}
                    selected={
                      updatedDateDescription[entry._id]?.startdate
                        ? updatedDateDescription[entry._id].startdate
                        : entry.startdate
                          ? new Date(entry.startdate)
                          : null
                    }
                    onChange={(date) =>
                      handleDescriptionDateChange(date, "startdate", entry)
                    }
                  />
                )}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center">
                {/* {entry.enddate ? formatDateByCountry(entry.enddate) : ""} */}
                {entry._isTrainingCost || entry.isTaskComplete ? (
                  <div className="text-black">
                    {entry.enddate ? formatDateByCountry(entry.enddate) : ""}
                  </div>
                ) : (
                  <DatePicker
                    dateFormat={getDateFormat()}
                    disabled={isViewOnly}
                    className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md bg-white ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                    placeholderText={getDatePlaceholder()}
                    popperClassName="z-[1500]"
                    minDate={
                      // ✅ Use updated startdate if available, else fallback to entry.startdate
                      updatedDateDescription[entry._id]?.startdate
                        ? new Date(updatedDateDescription[entry._id].startdate)
                        : entry.startdate
                          ? new Date(entry.startdate)
                          : null
                    }
                    selected={
                      updatedDateDescription[entry._id]?.enddate
                        ? updatedDateDescription[entry._id].enddate
                        : entry.enddate
                          ? new Date(entry.enddate)
                          : null
                    }
                    onChange={(date) =>
                      handleDescriptionDateChange(date, "enddate", entry)
                    }
                  />
                )}
              </div>

              <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                {entry._isTrainingCost || entry.isTaskComplete ? (
                  <span>{formatAmountComma(entry.expenses) || ""}</span>
                ) : (
                  <input
                    type="text"
                    disabled={isViewOnly}
                    value={getOngoingExpenseInputDisplay(
                      entry,
                      editedDescription[entry._id]
                    )}
                    placeholder="enter expense"
                    className={`border border-gray-200 w-full rounded-md max-w-full p-1 text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                    onChange={(e) => {
                      if (isViewOnly) return;
                      const value = validateAmountValue(e.target.value);
                      const amountLimit = parseAmountNumber(entry.value);
                      const expenseNum = parseAmountNumber(value);
                      if (
                        value !== "" &&
                        amountLimit > 0 &&
                        expenseNum > amountLimit
                      ) {
                        enqueueSnackbar("Expenses is exceeding the Amount", {
                          variant: "error",
                        });
                      }
                      if (expenseNum < 0) return;
                      setEditedDescription((prev) => ({
                        ...prev,
                        [entry._id]: value, // store by task id
                      }));
                    }}
                    onBlur={() => handleDescriptionExpensesUpdate(entry._id)}
                  />
                )}
              </div>
                            <div
                title={`${entry.notes}`}
                className="p-1 border-b border-[#dee2e6] w-[10%] text-center break-words whitespace-normal max-h-14 overflow-auto"
              >
                {/* {entry.notes || ""} */}
                {entry._isTrainingCost || entry.isTaskComplete ? (
                  <span>{entry.notes || ""}</span>
                ) : (
                  <input
                    type="text"
                    disabled={isViewOnly}
                    value={
                      editedDescriptionNotes[entry._id] ?? entry.notes ?? ""
                    }
                    placeholder="enter note"
                    className={`border border-gray-200 w-full max-w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                    onChange={(e) => {
                      if (isViewOnly) return;
                      const value = e.target.value;
                      setEditedDescriptionNotes((prev) => ({
                        ...prev,
                        [entry._id]: value,
                      }));
                    }}
                    onBlur={() => handleDescriptionNotesUpdate(entry._id)}
                  />
                )}
              </div>
              <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
                {entry._isTrainingCost || entry.isTaskComplete ? (
                  <ProgressTracker
                    value={(() => {
                      const rawValue =
                        (getOngoingExpenseNumberForTracker(
                          entry,
                          editedDescription[entry._id]
                        ) /
                          entry.value) *
                        100;
                      return rawValue < 100 ? 100 : rawValue;
                    })()}
                  />
                ) : (
                  <ProgressTracker
                    value={
                      (getOngoingExpenseNumberForTracker(
                        entry,
                        editedDescription[entry._id]
                      ) /
                        entry.value) *
                      100
                    }
                  />
                )}
              </div>
              <div
                className="p-1 border-b border-[#dee2e6] w-[10%] text-center cursor-default"
                title={`${entry.isTaskComplete ? "Completed" : "Pending"}`}
              >
                {/* <Checkbox
                  disabled={row.Assignedperson?.every(
                    (person) => person.isProjectComplete === true
                  )}
                  className="cursor-default pointer-events-none"
                  readOnly
                  checked={entry.isTaskComplete}
                /> */}
                {entry._isTrainingCost ? (
                  <Checkbox disabled checked />
                ) : loadingRows[index] ? (
                  <LoadingSpinner />
                ) : (
                  <Checkbox
                    disabled={
                      isViewOnly ||
                      row.Assignedperson?.every(
                        (person) => person.isProjectComplete === true
                      ) ||
                      (getOngoingExpenseNumberForTracker(
                        entry,
                        editedDescription[entry._id]
                      ) /
                        entry.value) *
                      100 <
                      1
                    }
                    checked={entry.isTaskComplete}
                    onChange={(e) => {
                      if (isViewOnly) return;
                      const isChecked = e.target.checked;
                      const currentExpenses = getOngoingExpenseNumberForTracker(
                        entry,
                        editedDescription[entry._id]
                      );
                      const startDate =
                        updatedDateDescription[entry._id]?.startdate ||
                        entry.startdate;
                      const endDate =
                        updatedDateDescription[entry._id]?.enddate ||
                        entry.enddate;

                      if (isChecked) {
                        // ✅ Only validate when marking complete
                        if (!startDate || !endDate) {
                          enqueueSnackbar("Please Select End Date", {
                            variant: "error",
                          });
                          return;
                        }

                        if (!currentExpenses || currentExpenses <= 0) {
                          enqueueSnackbar("Please Enter Expense", {
                            variant: "error",
                          });
                          return;
                        }

                        handlemarkDescriptionComplete(entry, index);
                        handleDescriptionDateSubmit(entry);
                      } else {
                        handlemarkNotDescriptionComplete(entry);
                      }
                    }}
                  />
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
            <p className="text-red-500 text-center">
              {" "}
              No Tracking Task Entries Found
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpTaskTrackOtherExpense;
